import React, { useState, useEffect } from "react";
import DefaultButton from "../../../../../components/buttons/defaultButton";
import { Icons } from "../../../../../../utils/icons";
import { useNavigate } from "react-router-dom";
import NoData from "../../../../../components/noData/noData";
import CommonModal from "../../../../../components/modals/commonModel";
import CommonForm from "../../../../../components/form/form";
import {
  createBankAccount,
  updateBankAccount,
  getBankAccountList,
  deleteBankAccount,
} from "../../../../../../services/workspace/user.service";
import toast from "react-hot-toast";
import useCommonFetch from "../../../../../../hooks/useCommonFetch";

const DetailRow = ({ label, value }) => (
  <div className="grid grid-cols-2">
    <span className="text-sm text-gray-500">{label}</span>
    <span className="text-sm font-semibold text-blue-900">{value || "-"}</span>
  </div>
);

const bank = [{}];

const DetailSkeleton = () => (
  <div className="grid grid-cols-2 animate-pulse">
    <div className="h-5 bg-gray-300 rounded w-3/4 mb-2" />
    <div className="h-5 bg-gray-200 rounded w-2/3 mb-2" />
  </div>
);

const BankCard = ({ staff, heandleFetchData }) => {
  const navigate = useNavigate();
  const [bankList, setBankList] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [loading, setLoading] = useState(false);
  const { handleCreate, handleUpdate, handleDelete } = useCommonFetch();

  // Fetch bank accounts on mount
  useEffect(() => {
    if (staff?.id) {
      fetchBankAccounts();
    }
  }, [staff?.id]);

  const fetchBankAccounts = async () => {
    try {
      setLoading(true);
      const response = await getBankAccountList({ user_id: staff.id });
      // Handle different response structures
      const banks = response?.bankAccount?.data||[];
      
      if (Array.isArray(banks) && banks.length > 0) {
        // Map API response to form field names
        const mappedBanks = banks.map((bank) => ({
          id: bank.id || bank.uuid,
          uuid: bank.uuid,
          bank_name: bank.name,
          holder_name: bank.account_holder,
          account_no: bank.account_number,
          ifsc_code: bank.ifsc_code,
          branch_name: bank.branch,
          pan_no: bank.pan,
          aadhaar_no: bank.aadhar,
          is_default: bank.is_default || false,
        }));
        setBankList(mappedBanks);
      } else {
        setBankList([]);
      }
    } catch (error) {
      console.error("Error fetching bank accounts:", error);
      toast.error("Failed to fetch bank accounts");
      setBankList([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col gap-2 rounded-md h-full">
        {Array.from({ length: 3 }).map((_, idx) => (
          <DetailSkeleton key={idx} />
        ))}
      </div>
    );
  }

  const bankFields = [
    { name: "bank_name", label: "Bank Name", type: "text", required: true, colSpan: 6 },
    { name: "holder_name", label: "Account Holder Name", type: "text", required: true, colSpan: 6 },
    { name: "account_no", label: "Account Number", type: "text", required: true, colSpan: 6 },
    { name: "ifsc_code", label: "IFSC Code", type: "text", required: true, colSpan: 6 },
    { name: "branch_name", label: "Branch Name", type: "text", colSpan: 6, required: true },
    { name: "pan_no", label: "PAN Number", type: "text", colSpan: 6, required: true },
    { name: "aadhaar_no", label: "Aadhaar Number", type: "text", colSpan: 12, required: true },
  ];

  // ADD BANK
  // ---------------------
  const handleAdd = () => {
    setEditData(null);
    setModalOpen(true);
  };

  // ---------------------
  // EDIT BANK
  // ---------------------
  const handleEdit = (bank) => {
    // Map bank data to form field names
    const editFormData = {
     
      id: bank.id ,
      uuid: bank.uuid,
      bank_name: bank.bank_name || bank.name,
      holder_name: bank.holder_name || bank.account_holder,
      account_no: bank.account_no || bank.account_number,
      ifsc_code: bank.ifsc_code,
      branch_name: bank.branch_name || bank.branch,
      pan_no: bank.pan_no || bank.pan,
      aadhaar_no: bank.aadhaar_no || bank.aadhar,
    };
    setEditData(editFormData);
    setModalOpen(true);
  };

  // ---------------------
  // DELETE BANK
  // ---------------------
  const handleDeleteBank = async (bank) => {
    try {
      // Prefer UUID for delete (most APIs use UUID in the URL); fall back to numeric id
      const deleteId = bank?.uuid || bank?.id;
      if (!deleteId) {
        console.error("No valid id/uuid found for bank record", bank);
        toast.error("Unable to delete bank account. Missing identifier.");
        return;
      }

      await handleDelete(deleteId, deleteBankAccount, {
        successMessage: "Bank account deleted successfully!",
        errorMessage: "Failed to delete bank account!",
      });
      await fetchBankAccounts();
      if (heandleFetchData) {
        heandleFetchData();
      }
    } catch (error) {
      console.error("Error deleting bank account:", error);
    }
  };

  // ---------------------
  // SET DEFAULT BANK
  // ---------------------
  const handleSetDefault = (bank) => {
    setBankList((prev) =>
      prev.map((item) => ({
        ...item,
        is_default: item.id === bank.id,
      }))
    );
  };

  // ---------------------
  // SUBMIT FORM (ADD or EDIT)
  // ---------------------
  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      // Map form fields to API payload format
      const payload = {
        user_id: staff.id,
        name: values.bank_name,
        branch: values.branch_name || "",
        account_holder: values.holder_name,
        account_number: values.account_no,
        ifsc_code: values.ifsc_code,
        pan: values.pan_no || "",
        aadhar: values.aadhaar_no || "",
      };

      if (editData && editData.id) {
        // Update existing bank account
        await handleUpdate(payload, editData.uuid, updateBankAccount, {
          successMessage: "Bank account updated successfully!",
          errorMessage: "Failed to update bank account!",
        });
      } else {
        // Create new bank account
        await handleCreate(payload, createBankAccount, {
          successMessage: "Bank account added successfully!",
          errorMessage: "Failed to add bank account!",
        });
      }

      // Refresh bank accounts list
      await fetchBankAccounts();
      
      // Refresh parent data if callback provided
      if (heandleFetchData) {
        heandleFetchData();
      }

      // Reset form and close modal
      resetForm();
      setEditData(null);
      setModalOpen(false);
    } catch (error) {
      console.error("Error in handleSubmit:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-1 rounded-md h-full">
      {/* Header */}
      <div className="rounded-md flex justify-between items-center bg-white px-4 py-2">
        <h2 className="text-lg font-semibold text-blue-900">Bank Details</h2>

        <DefaultButton name={Icons.ADD} title="Add Bank" onClick={handleAdd} />
      </div>

      {/* No Data */}
      <div className="w-full bg-white item-center justify-center flex">
        {bankList.length === 0 && <NoData title="Bank Details Not Found" message="Please add bank details" buttonText="Add Bank" onClick={handleAdd}/>}
      </div>

      {/* Bank Cards */}
      <div className="grid gap-2">
        {bankList.map((bank) => (
          <div
            key={bank.id}
            className={`p-4 bg-white border border-borderColor rounded-md ${
              bank.is_default ? "border-blue-600" : ""
            }`}
          >
            {/* Card Header */}
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-md font-semibold text-blue-900">
                {bank.bank_name || "Bank"}
              </h3>

              <div className="flex gap-2">
                <DefaultButton
                  name={Icons.EDIT}
                  title="Edit"
                  onClick={() => handleEdit(bank)}
                />

                <DefaultButton
                  name={Icons.DELETE}
                  title="Delete"
                  onClick={() => handleDeleteBank(bank)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-y-2">
              {bankFields.map((item, idx) => (
                <DetailRow key={idx} label={item.label} value={bank[item.name]} />

              ))}
            </div>

            {/* Default Button */}
            <div className="mt-4">
              {bank.is_default ? (
                <span className="text-xs px-3 py-1 bg-blue-100 text-blue-800 rounded-full">
                  Default Bank
                </span>
              ) : (
                <button
                  className="text-xs px-3 py-1 border border-blue-600 text-blue-600 rounded hover:bg-blue-600 hover:text-white transition"
                  onClick={() => handleSetDefault(bank)}
                >
                  Set as Default
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <CommonModal
        show={modalOpen}
        onClose={() => setModalOpen(false)}
        headerTitle={editData ? "Edit Bank" : "Add Bank"}
        modalSize="lg"
      >
        <CommonForm
          fields={bankFields}
          initialData={editData || {}}
          onSubmit={handleSubmit}
          submitButtonText={editData ? "Update" : "Save"}
          onCancel={() => setModalOpen(false)}
        />
      </CommonModal>
    </div>
  );
};

export default BankCard;
